import type { Action } from '../interaction/actions';
import type { AudioChannel } from './audioManager';
import type { StateTag } from '../scene/tagging';
import type { Variation } from './soundscape';

// A time-of-day audio cue — the unit of the CUES lane. Where the per-state
// beds (states.json) answer "what loops in each era", a cue answers "what
// fires at this moment of the day". It reuses the interaction Action
// vocabulary, so a scheduled cue can do anything a click can (play a sound,
// stop a channel, swap state, log…) — time-events and interaction-events
// are the same leaves, just triggered differently.
//
// Live cues come from public/soundscape.json (the `cues` array) — see
// soundscapeStore; audio-cues.json is only a read-only migration fallback now.
// The CueScheduler fires a cue when the playhead crosses its `t` going forward
// (while running), optionally gated to one era.
export interface AudioCue {
  /** Stable id — keys the marker + survives reload for edit/delete. */
  id: string;
  /** Time of day in [0,1) when this cue fires. */
  t: number;
  /** Era gate: only fires when stateController.current matches. `'both'` or
   *  undefined = fires in either era. */
  state?: StateTag;
  /** Optional display label; the lane falls back to the played asset id. */
  label?: string;
  /** What to do — the same leaves the interaction engine runs on a click. */
  actions: Action[];
  /** Per-fire variation: chance to fire, oneOf asset pool (swaps the first
   *  audio.play id), detune, and a small random pre-fire delay (jitterSec). */
  vary?: Variation;
}

const wrap01 = (v: number): number => ((v % 1) + 1) % 1;

/** A fresh, collision-resistant cue id. Browser context, so Math.random is
 *  fine here (unlike workflow scripts). */
export function newCueId(): string {
  return 'cue_' + Math.random().toString(36).slice(2, 9);
}

const CUE_STATES: StateTag[] = ['past', 'present', 'both'];

/** Sanitize a cue's era gate at load (S9). A valid 'past'|'present'|'both' is
 *  kept; a typo'd value is WARNED and dropped (→ undefined = no era gate, fires
 *  per its remaining gates) instead of silently never matching either era (and
 *  rendering hollow with a misleading "not in current era" tooltip in BOTH).
 *  Deliberately NOT coerced to 'both' — the warn is the point, so the author
 *  notices and restores the intended era rather than the cue quietly firing
 *  everywhere. */
export function sanitizeCueState(state: unknown, cueId: string): StateTag | undefined {
  if (state === undefined || state === null) return undefined;
  if (typeof state === 'string' && CUE_STATES.includes(state as StateTag)) return state as StateTag;
  console.warn(`[cues] cue "${cueId}" has an invalid era gate ${JSON.stringify(state)} — dropped (cue now fires in both eras); fix the typo to re-gate it`);
  return undefined;
}

/** Load + validate cues from public/audio-cues.json. Mirrors loadStateConfig:
 *  missing/!ok file → empty list (the feature is simply dormant until the
 *  first cue is authored + saved). Accepts either a bare array or
 *  { cues: [...] }; skips entries missing a numeric t or an actions array. */
export async function loadAudioCues(url = '/audio-cues.json'): Promise<AudioCue[]> {
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) {
      console.warn('[cues] no cues at ' + url + '; starting empty');
      return [];
    }
    const data = await res.json();
    const raw: unknown[] = Array.isArray(data) ? data : (data?.cues ?? []);
    const out: AudioCue[] = [];
    for (const r of raw) {
      const c = r as Partial<AudioCue>;
      if (typeof c.t !== 'number' || !Array.isArray(c.actions)) continue;
      const id = typeof c.id === 'string' ? c.id : newCueId();
      out.push({
        id,
        t: wrap01(c.t),
        state: sanitizeCueState(c.state, id),
        label: c.label,
        actions: c.actions,
      });
    }
    return out;
  } catch (e) {
    console.warn('[cues] load failed; starting empty', e);
    return [];
  }
}

/** The channel a cue's audio touches — colors its marker in the same language
 *  as the SOUND lane + 3D source spheres. */
export function cueChannel(cue: AudioCue): AudioChannel | undefined {
  for (const a of cue.actions) {
    if (a.kind === 'audio.play' || a.kind === 'audio.playFromUserData') return a.channel;
    if (a.kind === 'audio.stopChannel') return a.channel;
  }
  return undefined;
}

/** The asset id a cue plays (first audio.play), for labeling its marker. */
export function cueAssetId(cue: AudioCue): string | undefined {
  for (const a of cue.actions) if (a.kind === 'audio.play') return a.id;
  return undefined;
}

/** The hero a cue's first audio.play is spatially anchored to, if any — lets a
 *  CUES marker carry its anchor into the cross-surface focus (hover the marker
 *  → light that hero + its 3D source). */
export function cueHeroId(cue: AudioCue): string | undefined {
  for (const a of cue.actions) if (a.kind === 'audio.play') return a.at?.heroId;
  return undefined;
}
